/**
 * Login Page
 * Azure AD / Microsoft Sign-In with BGC branding
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Grid,
  Chip
} from '@mui/material';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShieldIcon from '@mui/icons-material/Shield';
import { useAuth } from '../context/AuthContext';
import { isMsalConfigured } from '../auth/msalConfig';
import packageJson from '../../package.json';

const currentYear = new Date().getFullYear();
const APP_VERSION = packageJson.version;

const features = [
  { icon: <VisibilityIcon />, label: 'Complete Visibility' },
  { icon: <AssessmentIcon />, label: 'Risk Assessment' },
  { icon: <SpeedIcon />, label: 'Streamlined Reviews' },
  { icon: <ShieldIcon />, label: 'Enhanced Security' }
];

function Login() {
  const { login, loading, error } = useAuth();
  const configured = isMsalConfigured();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(160deg, #0a1628 0%, #1a365d 40%, #0f2744 70%, #0a1628 100%)',
        position: 'relative',
        overflow: 'hidden',
        p: 3
      }}
    >
      {/* Background Effects */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none'
        }}
      />

      {/* Main Content */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 520,
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Logo & Branding */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          {/* ATLAS Logo */}
          <Box
            component="img"
            src="/assets/logo.png"
            alt="ATLAS"
            sx={{
              height: 100,
              width: 100,
              objectFit: 'contain',
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.95)',
              p: 1,
              mb: 3,
              boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.4)'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              color: 'white',
              letterSpacing: '-0.02em',
              mb: 1
            }}
          >
            ATLAS
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255,255,255,0.5)', 
              fontWeight: 400,
              letterSpacing: '0.02em'
            }}
          >
            Account Tracking & Lifecycle Admin
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255,255,255,0.35)', 
              display: 'block',
              mt: 1
            }}
          >
            BGC Engineering Inc.
          </Typography>
        </Box>

        {/* Login Card */}
        <Box
          sx={{
            backdropFilter: 'blur(20px)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.1)',
            p: { xs: 4, sm: 5 },
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
          }}
        >
          {/* Lock Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 4,
              boxShadow: '0 15px 35px -10px rgba(59, 130, 246, 0.5)'
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          {/* Welcome Text */}
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: 'white', 
              textAlign: 'center',
              mb: 1
            }}
          >
            Welcome Back
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255,255,255,0.5)', 
              textAlign: 'center',
              mb: 4
            }}
          >
            Sign in with your BGC account to continue
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                bgcolor: 'rgba(239, 68, 68, 0.15)',
                color: '#fca5a5',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                '& .MuiAlert-icon': {
                  color: '#f87171'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Not Configured Alert */}
          {!configured && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 3,
                bgcolor: 'rgba(245, 158, 11, 0.15)',
                color: '#fcd34d',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                '& .MuiAlert-icon': {
                  color: '#fbbf24'
                }
              }}
            >
              Azure AD authentication is not configured. Contact IT.
            </Alert>
          )}

          {/* Sign In Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={login}
            disabled={loading || !configured}
            startIcon={loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : <MicrosoftIcon />}
            sx={{
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2.5,
              bgcolor: 'white',
              color: '#1a1a1a',
              boxShadow: '0 10px 40px -10px rgba(255, 255, 255, 0.3)',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.95)',
                transform: 'translateY(-2px)',
                boxShadow: '0 15px 50px -10px rgba(255, 255, 255, 0.4)'
              },
              '&:disabled': {
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.5)'
              }
            }}
          >
            {loading ? 'Signing in...' : 'Sign in with Microsoft'}
          </Button>

          {/* Help Text */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.4)', 
              textAlign: 'center',
              mt: 3
            }}
          >
            Use your organization account to sign in
          </Typography>

          {/* Divider */}
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* Features */}
          <Grid container spacing={1.5} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item key={index}>
                <Chip
                  icon={React.cloneElement(feature.icon, { 
                    sx: { fontSize: 16, color: 'rgba(255,255,255,0.6) !important' } 
                  })}
                  label={feature.label}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.6)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    fontSize: '0.75rem',
                    '& .MuiChip-icon': {
                      ml: 1
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 5, textAlign: 'center' }}>
          <Stack 
            direction="row" 
            spacing={1} 
            justifyContent="center" 
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <SecurityIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              Protected by Microsoft Entra ID
            </Typography>
          </Stack>
          
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 3, mx: 8 }} />
          
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 0.5 }}>
            Developed by <strong>BGC Engineering Inc. — IT Team</strong>
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', display: 'block', mb: 0.5 }}>
            © {currentYear} BGC Engineering Inc. All rights reserved.
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>
            v{APP_VERSION}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
